/* The admin portal, in the artboard's light register.
 *
 * SOURCE: `Damga Admin Portal v2.dc.html` (SHA-256 d7d0e2bd…, 178,258 bytes).
 * The same register as the merchant dashboard, which is what the artboard
 * draws: white ground, ink #08282f, accent #0e7490, the same three faces. Its
 * own constants are still named `CREAM` and `ochre` from the palette this
 * project abandoned; the values in them are teal, and these are the values.
 *
 * A GROUPED SIDEBAR, which is the one structural difference from the dashboard:
 * twenty-one screens in five groups need the labels, where eight did not.
 *
 * DEPARTURES, ALL NAMED. The artboard is one 1440 x 960 frame; this is a real
 * page, so the column is fluid, the sidebar becomes a scrolling row under
 * 1000px, and nothing is positioned absolutely. Its mono labels sit at 9 and
 * 10px; nothing here is under 10px. Its tables assume data that does not exist,
 * so every one of them has an empty state underneath it in the same shape.
 */
/* One table as blocks, for a narrow box. Written once and placed under two
   container widths below: a table stacks when ITS box is too narrow for it,
   which for the nine-column merchants table is much wider than a phone. */
const stacked = (t: string) => `
  ${t}, ${t} tbody, ${t} tr, ${t} td { display: block; }
  ${t} thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
  ${t} tr { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 12px 14px; padding: 14px 2px; border-bottom: 1px solid var(--line); }
  ${t} tbody tr:hover { background: none; }
  ${t} td { padding: 0; border: 0; text-align: start; overflow-wrap: anywhere; }
  ${t} td[data-lead] { grid-column: 1 / -1; order: -1; }
  ${t} td[data-label]::before { content: attr(data-label); display: block; margin-bottom: 4px; font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.07em; text-transform: uppercase; color: var(--ink-faint); }
  ${t} td:not([data-label]):not([data-lead]) { grid-column: 1 / -1; }
`;

export const adminCss = `
@font-face {
  font-family: "Schibsted Grotesk";
  src: url("/fonts/SchibstedGrotesk-variable-latin.woff2") format("woff2");
  font-weight: 400 900;
  font-display: swap;
}
@font-face {
  font-family: "Bricolage Grotesque";
  src: url("/fonts/BricolageGrotesque-variable-latin.woff2") format("woff2");
  font-weight: 400 800;
  font-display: swap;
}
@font-face {
  font-family: "DM Mono";
  src: url("/fonts/DMMono-500-latin.woff2") format("woff2");
  font-weight: 400 600;
  font-display: swap;
}

:root {
  color-scheme: light;
  --font-sans: "Schibsted Grotesk", system-ui, -apple-system, sans-serif;
  --font-mono: "DM Mono", ui-monospace, SFMono-Regular, monospace;
  --font-display: "Bricolage Grotesque", "Schibsted Grotesk", sans-serif;

  --ink: #08282f;
  --ink-soft: #33565f;
  --ink-faint: rgba(8, 40, 47, 0.74);
  --accent: #0b5c74;
  --seal: #0e7490;
  --sky: #38bdf8;
  --fill: #5eead4;
  --ok: #0c6543;
  --warn: #845009;
  --danger: #993524;

  --paper: #ffffff;
  --panel: #f7fafb;
  --line: rgba(8, 40, 47, 0.12);
  --line-strong: rgba(8, 40, 47, 0.18);
  --tint: rgba(8, 40, 47, 0.05);
  --grad-accent: linear-gradient(160deg, #5eead4, #4dd6e8 55%, #38bdf8);
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
}

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; background: var(--paper); }
body { font-family: var(--font-sans); color: var(--ink); -webkit-font-smoothing: antialiased; }
h1, h2, h3 { font-family: var(--font-display); font-optical-sizing: auto; margin: 0; }
p { margin: 0; }
button { font: inherit; color: inherit; }
:focus-visible { outline: 2px solid var(--seal); outline-offset: 2px; }
table { border-collapse: collapse; width: 100%; }

.app { display: flex; min-height: 100dvh; }

/* --- the sidebar ------------------------------------------------------------ */
/* It holds still while the page scrolls, and its sections scroll inside it:
   24 sections at 44px do not fit a laptop's height, and before this the
   sidebar made the page taller than the screen and Outbox sat below the fold
   (review of 2026-09-18, findings 23 and 24). */
.side {
  flex: none;
  width: 246px;
  position: sticky;
  top: 0;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--panel);
  border-right: 1px solid var(--line);
}
.side-top { display: flex; align-items: center; justify-content: space-between; }
.side-brand { display: flex; align-items: center; gap: 10px; padding: 20px 18px 18px; }
.side-menu { display: none; }
.side-panel { display: flex; flex-direction: column; flex: 1 1 auto; min-height: 0; }
.mark {
  position: relative;
  display: block;
  flex: none;
  width: 22px;
  height: 22px;
  background: var(--seal);
  border-radius: 6px 6px 6px 1px;
  overflow: hidden;
}
.mark::before, .mark::after { content: ""; position: absolute; top: 6px; height: 11px; background: #eaf6f8; }
.mark::before { left: 6px; width: 7px; border-radius: 0 999px 999px 0; }
.mark::after { left: 4px; width: 2px; }
.side-word { font: 600 14px/1 var(--font-sans); letter-spacing: -0.025em; }
.side-nav { padding: 0 12px 12px; display: grid; gap: 2px; align-content: start; flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.side-group { display: grid; gap: 2px; padding-top: 12px; }
.side-group-label { padding: 0 13px 6px; font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.side-link {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  min-height: 44px;
  padding: 0 13px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--ink-soft);
  font: 500 13px/1 var(--font-sans);
  text-align: start;
  cursor: pointer;
}
.side-link:hover { background: rgba(8, 40, 47, 0.05); color: var(--ink); }
.side-link[aria-current="page"] { background: var(--paper); color: var(--ink); font-weight: 600; box-shadow: 0 6px 14px -10px rgba(8, 40, 47, 0.4), inset 0 0 0 1px var(--line); }
.side-foot { padding: 14px 16px; border-top: 1px solid var(--line); display: grid; gap: 8px; }
.side-who { font: 500 12px/1.4 var(--font-mono); color: var(--ink-faint); overflow-wrap: anywhere; }

/* Under 1000px: one bar, and a menu beneath it, as on the dashboard. It was a
   row of every section that scrolled sideways, cut words at its edge and ran
   the group names inline; "Sign out" broke onto two lines (finding 22). In the
   menu the groups sit side by side where there is room. */
@media (max-width: 1000px) {
  .app { flex-direction: column; }
  .side { position: static; height: auto; width: auto; border-right: 0; border-bottom: 1px solid var(--line); }
  .side-top { padding: 8px 12px 8px 18px; }
  .side-brand { padding: 0; }
  .side-menu {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    padding: 0 18px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: var(--paper);
    color: var(--ink);
    font: 600 13px/1 var(--font-sans);
    white-space: nowrap;
    cursor: pointer;
  }
  .side-menu[aria-expanded="true"] { background: var(--ink); color: var(--paper); border-color: var(--ink); }
  .side-panel { display: none; }
  .side-panel[data-open] { display: flex; border-top: 1px solid var(--line); }
  .side-nav {
    overflow: visible;
    padding: 4px 12px 12px;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 4px 16px;
  }
  .side-foot { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
  .side-foot .btn { flex: none; white-space: nowrap; }
}

/* --- the content column ------------------------------------------------------ */
/* One column that may shrink, here and on .panel: a grid's implicit column is as
   wide as its widest content, so a wide table pushed the page sideways instead of
   scrolling in .table-wrap. Found on the dashboard at 390 (2026-09-18); the same
   rule, fixed here before it was found here. */
.main { flex: 1 1 auto; min-width: 0; padding: 26px 32px 56px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 20px; align-content: start; }
@media (max-width: 700px) { .main { padding: 20px 16px 40px; } }
.head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.eyebrow { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--accent); margin: 0 0 7px; }
.title { font: 600 27px/1.1 var(--font-display); letter-spacing: -0.02em; }
.lead { font: 400 14px/1.55 var(--font-sans); color: var(--ink-soft); max-width: 62ch; }

.range { display: flex; gap: 6px; padding: 4px; border-radius: 14px; background: var(--tint); border: 1px solid var(--line); }
.range button { border: 0; border-radius: 10px; background: transparent; padding: 8px 12px; font: 600 12px/1 var(--font-sans); color: var(--ink-faint); cursor: pointer; }
.range button[aria-pressed="true"] { background: var(--paper); color: var(--ink); box-shadow: inset 0 0 0 1px var(--line); }

.stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; }
.stat { padding: 16px 18px; border-radius: 18px; background: var(--panel); border: 1px solid var(--line); }
.stat-v { font: 500 27px/1 var(--font-mono); font-variant-numeric: tabular-nums; margin: 0 0 8px; }
.stat-k { font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.07em; text-transform: uppercase; color: var(--ink-faint); }

.panel { padding: 20px 22px; border-radius: 20px; background: var(--paper); border: 1px solid var(--line); display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
.panel-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
.panel-title { font: 600 15px/1.2 var(--font-sans); }
.cols { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px; align-items: start; }

/* A count and what it counts, as a list rather than a wall of equal tiles:
   the label reads first and the figure is the answer to it. */
.counts { display: grid; gap: 0; margin: 0; }
.count { display: flex; align-items: baseline; justify-content: space-between; gap: 16px; padding: 11px 0; border-bottom: 1px solid var(--line); }
.count:last-child { border-bottom: 0; padding-bottom: 0; }
.count-k { font: 400 13.5px/1.35 var(--font-sans); color: var(--ink-soft); }
.count-v { margin: 0; font: 500 21px/1 var(--font-mono); font-variant-numeric: tabular-nums; }

.label { font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.mono { font-family: var(--font-mono); font-variant-numeric: tabular-nums; }

/* --- tables ------------------------------------------------------------------ */
/* position: relative, so a visually hidden header inside a table is contained by
   this scroller rather than stretching the page (found on the dashboard). */
.table-wrap { overflow-x: auto; position: relative; container-type: inline-size; }
/* A TABLE IN A NARROW BOX IS A LIST OF BLOCKS (review of 2026-09-18, findings
   20 and 21). The merchants table put "Send the owner a new link" one letter
   per line at 820 and every column but the name off a phone's edge, in a box
   that scrolled without saying so. Each row is a block when its box is too
   narrow: the lead across the top, every other value under its heading. The
   roles on every element keep it a table to a screen reader. */
@container (max-width: 560px) { ${stacked("table.table-stack")} }
@container (max-width: 940px) { ${stacked("table.table-stack[data-wide]")} }
/* Wide enough to be a table: the action never wraps. */
table.table-stack .row-actions .btn { white-space: nowrap; }
th {
  text-align: start;
  padding: 0 12px 10px;
  font: 600 11px/1.2 var(--font-mono);
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--ink-faint);
  border-bottom: 1px solid var(--line);
  white-space: nowrap;
}
td { padding: 13px 12px; border-bottom: 1px solid var(--line); font: 400 13px/1.4 var(--font-sans); vertical-align: top; }
td.num, th.num { text-align: end; font-family: var(--font-mono); font-variant-numeric: tabular-nums; }
tbody tr:hover { background: var(--tint); }
.cell-name { font-weight: 600; }
.cell-sub { display: block; font: 500 12px/1.4 var(--font-mono); color: var(--ink-faint); margin-top: 3px; }

.chip { display: inline-flex; align-items: center; padding: 5px 9px; border-radius: 999px; background: var(--tint); font: 600 11px/1 var(--font-mono); letter-spacing: 0.06em; text-transform: uppercase; color: var(--ink-soft); white-space: nowrap; }
.chip[data-tone="good"] { background: rgba(12, 101, 67, 0.12); color: var(--ok); }
.chip[data-tone="owed"] { background: rgba(14, 116, 144, 0.12); color: var(--accent); }
.chip[data-tone="warn"] { background: rgba(132, 80, 9, 0.12); color: var(--warn); }

.rows { display: grid; gap: 10px; list-style: none; margin: 0; padding: 0; }
.row { display: grid; grid-template-columns: 34px 1fr auto; gap: 12px; align-items: center; padding: 12px 14px; border-radius: 14px; background: var(--panel); border: 1px solid var(--line); }
.dot { width: 34px; height: 34px; border-radius: 999px; display: flex; align-items: center; justify-content: center; background: rgba(14, 116, 144, 0.14); color: var(--accent); font: 600 13px/1 var(--font-mono); }
.dot[data-kind="reward"] { background: rgba(94, 234, 212, 0.35); color: #04282e; }
.dot[data-kind="back"] { background: rgba(153, 53, 36, 0.1); color: var(--danger); }
.row-title { font: 500 13px/1.3 var(--font-sans); }
.row-meta { font: 500 12px/1.3 var(--font-mono); color: var(--ink-faint); margin-top: 3px; }
.row-value { font: 600 12px/1 var(--font-mono); color: var(--ink-soft); white-space: nowrap; }

.bar { height: 8px; border-radius: 999px; background: var(--tint); overflow: hidden; }
.bar-fill { display: block; height: 100%; border-radius: 999px; background: var(--grad-accent); transform-origin: left center; transform: scaleX(var(--pct, 0)); }

/* --- empty states, which most of this dashboard is on day one ---------------- */
.empty { padding: 36px 24px; border-radius: 20px; background: var(--panel); border: 1px dashed var(--line-strong); display: grid; gap: 8px; justify-items: start; }
.empty-title { font: 600 16px/1.3 var(--font-sans); }
.empty-body { font: 400 13.5px/1.55 var(--font-sans); color: var(--ink-soft); max-width: 52ch; }

/* --- controls ---------------------------------------------------------------- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  height: 44px;
  padding: 0 18px;
  border: 0;
  border-radius: 14px;
  font: 600 13px/1 var(--font-sans);
  cursor: pointer;
  transition: transform 150ms var(--ease-out);
}
.btn:active:not(:disabled) { transform: scale(0.98); }
.btn-primary { background: var(--grad-accent); color: #04282e; box-shadow: 0 8px 18px -12px rgba(14, 116, 144, 0.8); }
.btn-secondary { background: var(--paper); color: var(--ink); border: 1px solid var(--line-strong); }
.btn-quiet { background: transparent; color: var(--ink-faint); }
.btn:disabled { opacity: 0.55; cursor: default; }

.field { display: grid; gap: 7px; max-width: 420px; }
.field-label { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }
.input { height: 46px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--line-strong); background: var(--paper); font: 400 15px/1 var(--font-sans); color: var(--ink); }
.input:focus-visible { border-color: var(--seal); box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.18); outline: 0; }
/* The browser's own placeholder grey measured 2.35:1 on this ground, which the
   proof caught. The same defect the landing had, and the same fix. */
.input::placeholder { color: rgba(8, 40, 47, 0.62); }
.search { max-width: 320px; }
.error { color: var(--danger); font: 500 13px/1.45 var(--font-sans); }

/* --- setting up a shop, and the outbox (2026-09-18) --------------------------- */
.form { display: grid; gap: 14px; max-width: 420px; }
.form .btn { justify-self: start; }
.form-problem { color: var(--danger); font: 500 13px/1.45 var(--font-sans); }
.form-said { color: var(--ok); font: 500 13px/1.45 var(--font-sans); }
.row-actions { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 4px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.outbox-body { font: 400 13px/1.6 var(--font-sans); color: var(--ink-soft); white-space: pre-wrap; overflow-wrap: anywhere; }
.outbox-link { color: var(--accent); font-weight: 600; }
.note { font: 400 13px/1.55 var(--font-sans); color: var(--ink-soft); }
.fine { font: 400 12px/1.5 var(--font-sans); color: var(--ink-faint); }

.banner { padding: 12px 16px; border-radius: 14px; background: rgba(132, 80, 9, 0.08); border: 1px solid rgba(132, 80, 9, 0.3); color: var(--warn); font: 500 13px/1.45 var(--font-sans); }

/* --- sign in ------------------------------------------------------------------ */
.signin { min-height: 100dvh; display: grid; place-items: center; padding: 40px 20px; background: linear-gradient(168deg, #ffffff, #f7fafb 60%, #eef4f5); }
.signin-card { width: min(420px, 100%); display: grid; gap: 16px; padding: 28px; border-radius: 22px; background: var(--paper); border: 1px solid var(--line); box-shadow: 0 30px 60px -40px rgba(8, 40, 47, 0.4); }
.provider { display: grid; grid-template-columns: 22px 1fr 22px; align-items: center; gap: 12px; height: 50px; padding: 0 16px; border-radius: 14px; border: 1px solid var(--line-strong); background: var(--paper); font: 600 14px/1 var(--font-sans); text-align: start; cursor: pointer; }
.provider-mark { display: flex; justify-content: center; color: var(--accent); font: 600 15px/1 var(--font-sans); }
.or { display: flex; align-items: center; gap: 12px; }
.or::before, .or::after { content: ""; flex: 1; height: 1px; background: var(--line); }
.or span { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--ink-faint); }

/* --- what a screen with nothing behind it says ------------------------------- */
/* Thirteen of the twenty-one are this. It is deliberately not styled as an
   error or as a placeholder waiting to fill: it is a statement, and it reads
   like the rest of the page. */
.nothing { display: grid; gap: 14px; }
/* The box hugs the sentence rather than stretching the width of the page: a
   dashed rectangle 1,100px wide around three lines of text reads as a slot
   waiting to be filled, which is the opposite of what these screens say. */
.nothing .empty, .main > .empty { max-width: 66ch; }
.nothing-mark { font: 600 11px/1 var(--font-mono); letter-spacing: 0.09em; text-transform: uppercase; color: var(--warn); }
.facts { display: grid; gap: 12px; }
.fact { padding: 18px 20px; border-radius: 18px; background: var(--panel); border: 1px solid var(--line); display: grid; gap: 7px; }
.fact-k { font: 600 11px/1.2 var(--font-mono); letter-spacing: 0.07em; text-transform: uppercase; color: var(--ink-faint); }
.fact-v { font: 600 17px/1.3 var(--font-sans); }
.fact-b { font: 400 13.5px/1.55 var(--font-sans); color: var(--ink-soft); max-width: 60ch; }

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after { animation-duration: 1ms !important; transition-duration: 1ms !important; }
}

.visually-hidden { position: absolute !important; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
`;
